import _ from 'lodash';

import {
  BASE_PATH,
  CREDENTIALS_PATH,
  PROJECT_PATH,
  REPLICATIONS_PATH,
  WATCH_COPY_PATH,
  WATCH_DELETE_PATH,
  WATCH_UPDATE_PATH,
} from '../../transport/services/config';
import { transport } from '../../transport/transport';
import { AdministrationPromise } from '../../types/administration';
import { ProjectInOrganizationId } from '../../types/entities';

import {
  Replication,
  ReplicationBodyFiltersPayload,
  ReplicationCredentialsResponse,
  ReplicationPayload,
  ReplicationPayloadWatcher,
  ReplicationWatcher,
  ReplicationWatcherType,
} from './replications.types';
import { getReplicationDestination } from './replications.utils';
import { WixMpResponse } from '../../types/http-responses';

export const normalizeReplicationWatcher = <T>(
  replicationWatcher: ReplicationPayloadWatcher<T>,
): ReplicationWatcher<T> => {
  if (!replicationWatcher) {
    return replicationWatcher as ReplicationWatcher<T>;
  }

  if (!replicationWatcher.on.bodyFilters) {
    return replicationWatcher as ReplicationWatcher<T>;
  }

  return {
    ...replicationWatcher,
    on: {
      ...replicationWatcher.on,
      bodyFilters: _.map(replicationWatcher.on.bodyFilters, (bodyFilter) => {
        return _.reduce(
          bodyFilter,
          (acc, bodyFilter) => ({
            ...acc,
            ...bodyFilter,
          }),
          {},
        );
      }),
    },
  };
};

const normalizeReplication = (replication: ReplicationPayload): Replication => {
  const platform = getReplicationDestination(replication.replica.platform);
  const copyWatcher = normalizeReplicationWatcher<ReplicationWatcherType.Copy>(
    replication.copy,
  );
  const deleteWatcher =
    normalizeReplicationWatcher<ReplicationWatcherType.Delete>(
      replication.delete,
    );
  const updateWatcher =
    normalizeReplicationWatcher<ReplicationWatcherType.Update>(
      replication.update,
    );

  return {
    ...(replication as any as Replication),
    platform,
    copy: copyWatcher,
    delete: deleteWatcher,
    update: updateWatcher,
  };
};

interface GetReplications {
  organizationId: string;
  projectId: string;
}

function getReplications({ organizationId, projectId }: GetReplications) {
  return transport
    .get<WixMpResponse<ReplicationPayload>>(
      `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${projectId}${REPLICATIONS_PATH}`,
    )
    .then(({ data }) =>
      _(data.payload)
        .map(normalizeReplication)
        .sortBy('dateUpdated')
        .reverse()
        .value(),
    );
}

function getReplication(
  { organizationId, projectId }: GetReplications,
  replicationId: string,
): Promise<Replication> {
  return transport
    .get<WixMpResponse<ReplicationPayload>>(
      `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${projectId}${REPLICATIONS_PATH}/${replicationId}`,
    )
    .then(({ data }) => normalizeReplication(data.payload));
}

// TODO: fix type
function createReplication(
  { organizationId, project }: ProjectInOrganizationId,
  payload: any,
) {
  return transport
    .post<WixMpResponse<ReplicationPayload>>(
      `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${project.id}${REPLICATIONS_PATH}`,
      payload,
    )
    .then(({ data }) => normalizeReplication(data.payload));
}

export function removeReplication(
  { organizationId, project }: ProjectInOrganizationId,
  replicationId: string,
): AdministrationPromise<void> {
  return transport.delete(
    `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${project.id}${REPLICATIONS_PATH}/${replicationId}`,
  );
}

function changeActiveStatus(
  { organizationId, project }: ProjectInOrganizationId,
  replicationId: string,
  newStatus: boolean,
) {
  const status = newStatus ? 'enable' : 'disable';

  return transport.put(
    `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${project.id}${REPLICATIONS_PATH}/${replicationId}/${status}`,
  );
}

function renameReplication(
  { organizationId, project }: ProjectInOrganizationId,
  replicationId: string,
  name: string,
): AdministrationPromise<void> {
  return transport.put(
    `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${project.id}${REPLICATIONS_PATH}/${replicationId}/name`,
    { name },
  );
}

function createReplicationCredentials(
  { organizationId, project }: ProjectInOrganizationId,
  payload,
): AdministrationPromise<ReplicationCredentialsResponse> {
  return transport.post(
    `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${project.id}${CREDENTIALS_PATH}`,
    payload,
  );
}

export interface UpdateReplicationCredentialsPayload {
  credential: string;
}

function updateReplicationCredentials(
  { organizationId, project }: ProjectInOrganizationId,
  replicationId: string,
  payload: UpdateReplicationCredentialsPayload,
): AdministrationPromise<ReplicationPayload> {
  return transport.put(
    `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${project.id}${REPLICATIONS_PATH}/${replicationId}${CREDENTIALS_PATH}`,
    payload,
  );
}

function updateReplicationCopyWatch(
  { organizationId, project }: ProjectInOrganizationId,
  replicationId: string,
  payload: ReplicationBodyFiltersPayload,
): AdministrationPromise<ReplicationPayload> {
  return transport.put(
    `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${project.id}${REPLICATIONS_PATH}/${replicationId}${WATCH_COPY_PATH}`,
    {
      bodyFilters: payload,
    },
  );
}

function updateReplicationDeleteWatch(
  { organizationId, project }: ProjectInOrganizationId,
  replicationId: string,
  payload: ReplicationBodyFiltersPayload,
): AdministrationPromise<ReplicationPayload> {
  return transport.put(
    `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${project.id}${REPLICATIONS_PATH}/${replicationId}${WATCH_DELETE_PATH}`,
    {
      bodyFilters: payload,
    },
  );
}

function updateReplicationUpdateWatch(
  { organizationId, project }: ProjectInOrganizationId,
  replicationId: string,
  payload: ReplicationBodyFiltersPayload,
): AdministrationPromise<ReplicationPayload> {
  return transport.put(
    `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${project.id}${REPLICATIONS_PATH}/${replicationId}${WATCH_UPDATE_PATH}`,
    {
      bodyFilters: payload,
    },
  );
}

export const replicationsService = {
  createReplication,
  getReplications,

  getReplication,

  removeReplication,

  changeActiveStatus,

  createReplicationCredentials,
  updateReplicationCredentials,

  renameReplication,

  updateReplicationCopyWatch,
  updateReplicationDeleteWatch,
  updateReplicationUpdateWatch,
};
